import { isApiError } from './api-error';

/**
 * User-facing copy for the API's error codes (design.md §3.8).
 *
 * The server's `message` is already human-readable, so this only overrides
 * the codes where the UI can say something more useful than a generic
 * refusal — mostly the policy guards, which are refusals a user needs
 * explaining rather than fixing.
 *
 * Placed beside `api-error.ts` rather than inside `features/users/` (where
 * design.md §5.1 listed it) because roles needs the same map:
 * `SUPER_ADMIN_ROLE_PROTECTED` is raised by the roles endpoints, and a
 * cross-feature import from `features/roles` into `features/users` would be
 * worse than one shared module. Flagged in the handoff.
 */

export const ERROR_CODES = {
  invalidCredentials: 'INVALID_CREDENTIALS',
  accountInactive: 'ACCOUNT_INACTIVE',
  passwordChangeRequired: 'PASSWORD_CHANGE_REQUIRED',
  superUserImmutable: 'SUPER_USER_IMMUTABLE',
  superAdminRoleProtected: 'SUPER_ADMIN_ROLE_PROTECTED',
  superAdminGrantForbidden: 'SUPER_ADMIN_GRANT_FORBIDDEN',
  forbidden: 'FORBIDDEN',
  notFound: 'NOT_FOUND',
  conflict: 'CONFLICT',
  validation: 'VALIDATION_ERROR',
  // --- feature 002 (002/design.md §5.0)
  plantProfileNotSet: 'PLANT_PROFILE_NOT_SET',
  unitConfigFrozen: 'UNIT_CONFIG_FROZEN',
  methodNotAllowed: 'METHOD_NOT_ALLOWED',
  // --- feature 010 (010/design.md §5.0b)
  taxGroupNotConfigured: 'TAX_GROUP_NOT_CONFIGURED',
  taxVersionNotEffective: 'TAX_VERSION_NOT_EFFECTIVE',
  taxFixedUnitMismatch: 'TAX_FIXED_UNIT_MISMATCH',
  taxComponentNatureFrozen: 'TAX_COMPONENT_NATURE_FROZEN',
  taxConfigInUse: 'TAX_CONFIG_IN_USE',
  /*
   * --- feature 012: the goods-receipt lifecycle (012/design.md §5.0, task S-4).
   *
   * Twelve codes, spelled from `backend/src/shared/error-codes.ts` verbatim rather than from
   * the design's table, so the two sides cannot drift.
   *
   * Every one of them is **named here but deliberately not overridden** — see
   * `CODE_FALLBACK_MESSAGES` below for why. The value of naming them is that a screen branches
   * on `ERROR_CODES.versionConflict` instead of a string literal.
   */
  insufficientStockForEdit: 'INSUFFICIENT_STOCK_FOR_EDIT',
  insufficientStockForVoid: 'INSUFFICIENT_STOCK_FOR_VOID',
  insufficientStockForReturn: 'INSUFFICIENT_STOCK_FOR_RETURN',
  returnExceedsReceived: 'RETURN_EXCEEDS_RECEIVED',
  editConflictsWithReturn: 'EDIT_CONFLICTS_WITH_RETURN',
  alreadyVoided: 'ALREADY_VOIDED',
  documentNotEditable: 'DOCUMENT_NOT_EDITABLE',
  receiptHasReturns: 'RECEIPT_HAS_RETURNS',
  documentHasLivePayments: 'DOCUMENT_HAS_LIVE_PAYMENTS',
  versionConflict: 'VERSION_CONFLICT',
  noChanges: 'NO_CHANGES',
  idempotencyKeyReused: 'IDEMPOTENCY_KEY_REUSED',
  // --- feature 021 (021/design.md §4). 016's MOULD_GEOMETRY_FROZEN is retired with the moulds.
  mouldSettingNotConfigured: 'MOULD_SETTING_NOT_CONFIGURED',
  blockLargerThanMould: 'BLOCK_LARGER_THAN_MOULD',
  // --- feature 011 Story G (011/design.md §5.0; gk-build-plan.md §3.2 W0)
  paymentAlreadyReversed: 'PAYMENT_ALREADY_REVERSED',
  // --- feature 011 Story E/I: a stale view under the settlement lock (§3.4). Named, not overridden.
  allocationConflict: 'ALLOCATION_CONFLICT',
  /*
   * --- feature 005: production consumption (005/design.md §5.2, amendment A-4, task F-2).
   *
   * **Seven codes, and no more** — §5.2 is explicit that every other refusal is a plain
   * `VALIDATION_ERROR` with a precise `details[].field` the form binds to. Spelled from
   * `backend/src/shared/error-codes.ts` (:601–709) verbatim rather than from the design's table,
   * so the two sides cannot drift.
   *
   * Named here so a screen branches on `ERROR_CODES.productionRunAlreadyReversed` instead of a
   * string literal. **Not one of them is an override** — see `CODE_FALLBACK_MESSAGES`, and read
   * the note on `consumptionVarianceNotAcknowledged` there before adding one.
   *
   * 005 also reuses, and never re-mints, `RECIPE_NOT_FOUND`, `RECIPE_NOT_USABLE`,
   * `RECIPE_BASIS_NOT_DECLARED`, `MOULD_SETTING_NOT_CONFIGURED`, `BLOCK_LARGER_THAN_MOULD`,
   * `PLAN_NOT_EDITABLE`, `IDEMPOTENCY_KEY_REUSED` and `VALIDATION_ERROR`.
   *
   * **Four of those eight exist only in this sentence.** `MOULD_SETTING_NOT_CONFIGURED`,
   * `BLOCK_LARGER_THAN_MOULD`, `IDEMPOTENCY_KEY_REUSED` and `VALIDATION_ERROR` are real members
   * above; **`RECIPE_NOT_FOUND`, `RECIPE_NOT_USABLE`, `RECIPE_BASIS_NOT_DECLARED` and
   * `PLAN_NOT_EDITABLE` are backend-only** — they are raised by 003's and 016's endpoints and no
   * screen has ever needed to branch on them, so they were never added here. A-4 gives F-2 §5.2's
   * **seven new** codes and nothing else, so they stay out.
   *
   * **Said explicitly because this list is exactly where a reader would be misled.** F-3's
   * composer must branch on `RECIPE_NOT_USABLE` to put the refusal on the recipe picker; whoever
   * writes it will reach for `ERROR_CODES.recipeNotUsable`, find nothing, and have to work out
   * whether the omission is deliberate. It is — **and adding the member is a correct, additive
   * change when a screen actually needs it.** Add the name, not a `MESSAGES` entry: 003's and
   * 016's servers compose those sentences with the recipe and the status in them.
   */
  planNotConfirmed: 'PLAN_NOT_CONFIRMED',
  insufficientStockForProduction: 'INSUFFICIENT_STOCK_FOR_PRODUCTION',
  productionShortStockBlocked: 'PRODUCTION_SHORT_STOCK_BLOCKED',
  consumptionVarianceNotAcknowledged: 'CONSUMPTION_VARIANCE_NOT_ACKNOWLEDGED',
  finishedGoodNotConfigured: 'FINISHED_GOOD_NOT_CONFIGURED',
  productionRunAlreadyReversed: 'PRODUCTION_RUN_ALREADY_REVERSED',
  planHasProductionRuns: 'PLAN_HAS_PRODUCTION_RUNS',
  /*
   * --- feature 013: weighbridge weights and document attachments (013/design.md §2.9, task F1a).
   *
   * **Eight codes**, spelled from `backend/src/shared/error-codes.ts` (:482–574) verbatim rather
   * than from the design's table, so the two sides cannot drift. Seven are the attachment half;
   * `weightVarianceNotAcknowledged` is the weights half's one code and is declared here with
   * them because §2.9 and §7.1 count eight and this file is F1a's to own — **F1b must not add a
   * ninth copy of it.** F1b owns `api/goods-receipts.ts` and nothing in this file.
   *
   * **Two 410s, and the difference between them is the point.** `ATTACHMENT_REMOVED` is a
   * document that was deliberately withdrawn, with an actor and a reason; `ATTACHMENT_CONTENT_
   * MISSING` is a record whose file has gone from the disk, which is an administrator's problem
   * and a restore-from-backup. Collapsing both into "this document is gone" would tell a store
   * keeper to stop looking for evidence that is in fact recoverable. Screens branch on these two
   * codes for the *heading*; the sentence under it is always the server's.
   *
   * **Not one of them is an override** — see `CODE_FALLBACK_MESSAGES` below.
   *
   * 013 also reuses, and never re-mints, `DOCUMENT_NOT_EDITABLE` (a voided receipt refuses both
   * an upload and a removal, and an already-removed attachment reuses it too), `NOT_FOUND`,
   * `FORBIDDEN` and `VALIDATION_ERROR`.
   */
  attachmentTypeRejected: 'ATTACHMENT_TYPE_REJECTED',
  attachmentLimitReached: 'ATTACHMENT_LIMIT_REACHED',
  attachmentTooLarge: 'ATTACHMENT_TOO_LARGE',
  attachmentDuplicate: 'ATTACHMENT_DUPLICATE',
  attachmentRemoved: 'ATTACHMENT_REMOVED',
  attachmentContentMissing: 'ATTACHMENT_CONTENT_MISSING',
  attachmentStorageWriteFailed: 'ATTACHMENT_STORAGE_WRITE_FAILED',
  weightVarianceNotAcknowledged: 'WEIGHT_VARIANCE_NOT_ACKNOWLEDGED',
  /*
   * --- feature 004: purchase orders (004/design.md §5.0).
   *
   * **Eleven codes**, spelled from `backend/src/shared/error-codes.ts` verbatim
   * rather than from the design's table, so the two sides cannot drift.
   *
   * **Not one of them is an override** — the server composes messages that name
   * the PO number, the status, the GRN numbers and the receipt count, which are
   * exactly what makes each refusal actionable, and a generic local string would
   * drop that detail. See `CODE_FALLBACK_MESSAGES` below for the rule.
   *
   * 004 also reuses `VALIDATION_ERROR`, `FORBIDDEN`, `NOT_FOUND`, `NO_CHANGES`,
   * `IDEMPOTENCY_KEY_REUSED`, `METHOD_NOT_ALLOWED` and `DOCUMENT_NOT_EDITABLE`
   * — none of those are re-minted here.
   */
  purchaseOrderNotMatchable: 'PURCHASE_ORDER_NOT_MATCHABLE',
  purchaseOrderVendorMismatch: 'PURCHASE_ORDER_VENDOR_MISMATCH',
  purchaseOrderLineMismatch: 'PURCHASE_ORDER_LINE_MISMATCH',
  receiptSpansTwoPurchaseOrders: 'RECEIPT_SPANS_TWO_PURCHASE_ORDERS',
  overReceiptNotAcknowledged: 'OVER_RECEIPT_NOT_ACKNOWLEDGED',
  purchaseOrderNotAmendable: 'PURCHASE_ORDER_NOT_AMENDABLE',
  purchaseOrderQuantityBelowReceived: 'PURCHASE_ORDER_QUANTITY_BELOW_RECEIVED',
  purchaseOrderLineHasReceipts: 'PURCHASE_ORDER_LINE_HAS_RECEIPTS',
  purchaseOrderVendorImmutable: 'PURCHASE_ORDER_VENDOR_IMMUTABLE',
  purchaseOrderHasReceipts: 'PURCHASE_ORDER_HAS_RECEIPTS',
  purchaseOrderRevisionConflict: 'PURCHASE_ORDER_REVISION_CONFLICT',
} as const;

const MESSAGES: Record<string, string> = {
  [ERROR_CODES.superUserImmutable]: 'This account is protected and cannot be modified.',
  [ERROR_CODES.superAdminRoleProtected]:
    'This role is protected. It cannot be renamed, re-permissioned or deleted.',
  /**
   * Covers three things, all of which are the same escalation risk: granting
   * the Super Admin role, revoking it, and **resetting the password of
   * someone who holds it** (which would otherwise let a narrow
   * `user.reset_password` operator take over a total-access account).
   */
  [ERROR_CODES.superAdminGrantForbidden]:
    'Only a Super Admin can grant or revoke the Super Admin role, or reset the password of someone who holds it.',
  [ERROR_CODES.passwordChangeRequired]:
    'You must set a new password before you can use the application.',

  /**
   * Feature 002. All three are **refusals a user needs explaining rather than
   * fixing**, which is this map's stated bar — the server's own message is
   * accurate but assumes it is read by whoever raised it.
   */

  /**
   * 409 from receipt calculate and post. Deliberately does not name the
   * screen: the entry page swaps in its own blocking `Result` with a link to
   * `/settings/plant` (§6.5), and this copy is the fallback for anywhere else
   * the code can surface.
   */
  [ERROR_CODES.plantProfileNotSet]:
    "The plant's GST details have not been set up yet. A receipt cannot be taxed until the plant's state is recorded.",
  /**
   * 409 from a base-unit or conversion-factor change on a material that
   * already has stock (§4.4). The stored stock is denominated in the old unit,
   * so changing it would silently reinterpret every existing quantity.
   */
  [ERROR_CODES.unitConfigFrozen]:
    'This material already has stock, so its base unit and existing conversion factors cannot be changed. You can still add a new unit.',
  /** 405 on any mutating verb against a posted goods receipt (§5.7). */
  [ERROR_CODES.methodNotAllowed]:
    'A posted goods receipt cannot be changed. Record a correction instead (not yet available).',
};

/**
 * Feature 010's five codes (010/design.md §5.0b) live **here**, not in
 * `MESSAGES` above, and the distinction is the whole point.
 *
 * `MESSAGES` *overrides* the server. That is right for a policy refusal whose
 * server text assumes it is read by whoever raised it. It is wrong for all
 * five of these, because the server composes each one to **name the thing to
 * fix and who can fix it** — the material and the line index, the group and
 * the date, both units, the count of published versions referencing a
 * component — and ends the two posting failures with *"Ask an administrator
 * to assign a tax group"*, which a Store Keeper needs precisely because they
 * hold neither `tax_config` code and cannot self-serve (§4.7, Story G).
 * Overriding any of them would replace a sentence naming a material with a
 * sentence naming nothing.
 *
 * So these are **fallbacks**: consulted only if a response ever arrives
 * carrying the code without a message, so the user still reads something
 * about tax configuration rather than "Something went wrong".
 */
const CODE_FALLBACK_MESSAGES: Record<string, string> = {
  /** 422 on receipt calculate/post: no tax group, or an inactive one. */
  [ERROR_CODES.taxGroupNotConfigured]:
    'This material has no usable tax group, so the receipt cannot be taxed. Ask an administrator to assign a tax group.',
  /** 422: no version of the group is in force on the receipt date. */
  [ERROR_CODES.taxVersionNotEffective]:
    'This material’s tax group has no version in force on the receipt date, and there is no fallback rate. Ask an administrator to publish a version covering that date.',
  /** 422: a `fixed` (per-unit) component's unit is not the line's base unit. */
  [ERROR_CODES.taxFixedUnitMismatch]:
    'This tax group charges a fixed amount per unit, and that unit is not this material’s base unit. The quantity is never converted, so the receipt is refused rather than taxed on the wrong unit.',
  /** 409 on a `nature` change to a component a published version uses. */
  [ERROR_CODES.taxComponentNatureFrozen]:
    'This component is already used by a published tax version, so its nature is frozen. The nature is what the calculator reasons over, and changing it would silently reinterpret history.',
  /** 409 on deleting a component that is referenced anywhere. */
  [ERROR_CODES.taxConfigInUse]:
    'This component is referenced by published tax configuration and cannot be deleted. Deactivate it instead — existing versions keep working and it stops being offered for new ones.',

  /*
   * --- feature 012's twelve codes (012/design.md §5.0, task S-4).
   *
   * **All twelve are fallbacks, and none is an override.** S-4 says "their messages"; this is
   * what that has to mean for this feature, and the reason is in 012's own design: the envelope
   * was deliberately NOT widened, so each refusal carries its substance in a
   * **server-composed** `message` and one `details` entry per offending line. Every one of the
   * twelve `AppError` subclasses in `shared/errors.ts` takes `message` as a **required**
   * constructor argument — not one of them has a default — so the server always says something,
   * and it says more than this file can:
   *
   *   "GRN-2026-000015 is now at version 3; this correction was entered against version 2.
   *    Someone else has changed it. Reopen the receipt to see the current version, then
   *    re-enter your correction."
   *
   * An entry in `MESSAGES` would replace that with a sentence naming no document and no
   * version. That is the same trap 010's five avoided, for the same reason, and a stock refusal
   * would lose its arithmetic ("… would leave −3,000.000 kg on hand …") on top.
   *
   * So these are consulted **only** if a response ever arrives carrying the code with no
   * message, and each one is written to still name the class of refusal and the way out.
   */
  /** 409 — an edit would drive on-hand negative. */
  [ERROR_CODES.insufficientStockForEdit]:
    'This correction would leave negative stock, because material received on this receipt has already been used. Reduce the correction, or correct the consumption first.',
  /** 409 — a void would drive on-hand negative. */
  [ERROR_CODES.insufficientStockForVoid]:
    'Voiding this receipt would leave negative stock, because material it brought in has already been used. The consumption has to be corrected first.',
  /** 409 — a return exceeds what is physically in the yard. */
  [ERROR_CODES.insufficientStockForReturn]:
    'There is not enough of this material in stock to send back. Some of what this receipt brought in has already been used.',
  /** 422 — cumulative returns would exceed the parent line's current quantity. */
  [ERROR_CODES.returnExceedsReceived]:
    'This would send back more than the receipt brought in, counting returns already made against it. Check the quantity.',
  /** 409 — an edit contradicts a live return against the receipt. */
  [ERROR_CODES.editConflictsWithReturn]:
    'This correction contradicts a purchase return already raised against this receipt. Void the return first, then correct the receipt.',
  /** 409 — voiding a document that is already voided. */
  [ERROR_CODES.alreadyVoided]:
    'This document has already been voided. Its number and its lines are kept; nothing further is needed.',
  /**
   * 409 — edit or return against a voided receipt; edit of a purchase return (there is no
   * route); edit of a superseded version.
   */
  [ERROR_CODES.documentNotEditable]:
    'This document cannot be changed as it stands. A voided receipt, a purchase return and a superseded version are all read-only.',
  /** 409 — a void blocked by a live purchase return. */
  [ERROR_CODES.receiptHasReturns]:
    'This receipt has a live purchase return against it and cannot be voided. Void the return first — that frees the receipt.',
  /** 409 — a void blocked by a live payment allocation (feature 011 onward). */
  [ERROR_CODES.documentHasLivePayments]:
    'This document has a live payment against it and cannot be voided. The payment has to be released first.',
  /** 409 — `expectedVersion` does not match the row read under the lock. */
  [ERROR_CODES.versionConflict]:
    'Somebody else changed this document while you had it open, so your correction was not applied. Reopen it to see the current version, then re-enter your change.',
  /** 422 — an edit whose canonical input is identical to the current document. */
  [ERROR_CODES.noChanges]:
    'This correction is identical to the document as it stands, so there is nothing to record. Change a value, or close the form.',
  /** 409 — the idempotency key is on record against a different operation or target. */
  [ERROR_CODES.idempotencyKeyReused]:
    'This action could not be submitted safely because its request key has already been used for something else. Close the form and start again.',

  /*
   * --- feature 021's two codes (021/design.md §4). **Fallbacks, not overrides**, for 012's
   * reason: the server's message is the better sentence. `BLOCK_LARGER_THAN_MOULD` names both
   * volumes ("block volume 7.200000 m³ exceeds the mould cake 4.560 m³") and renders under the
   * L/W/H group; `MOULD_SETTING_NOT_CONFIGURED` renders as a notice with a link to
   * `/settings/mould`, not a toast (§7).
   */
  /** 422 — plan calculate/create and the calculator, before the setting has ever been saved. */
  [ERROR_CODES.mouldSettingNotConfigured]:
    'The plant mould setting has not been saved yet. Set it in Settings → Mould setting.',
  /** 422 — the block's volume is larger than one mould's cake. */
  [ERROR_CODES.blockLargerThanMould]:
    'This block is larger than the mould: its volume exceeds one mould’s cake volume. Check the block size.',

  /*
   * --- feature 011 Story G. **A fallback, not an override**, for 012's reason: the server's
   * message names the payment number and when it was reversed.
   */
  /** 409 — reversing, or allocating on account from, a payment that is already reversed. */
  [ERROR_CODES.paymentAlreadyReversed]:
    'This payment has already been reversed, so nothing further can be done with it. Reload to see its current state; to pay the party again, record a new payment.',

  /*
   * --- feature 005's seven codes (005/design.md §5.2, amendment A-4, task F-2).
   *
   * **Six fallbacks here, no overrides in `MESSAGES`, and one code deliberately absent from both
   * maps.** 012's and 010's reasoning applies to all of them: every refusal in §5.2 carries its
   * substance in a **server-composed** `message` plus one `details` entry per offending line —
   * the material and its on-hand and shortfall, the plan's current status, the percentage a line
   * breached by, where to configure the finished good, which runs block a plan's cancellation.
   * An entry in `MESSAGES` would replace a sentence naming a material and an arithmetic with a
   * sentence naming nothing, and `describeError` returns a `MESSAGES` hit **before** it looks at
   * `error.message`.
   *
   * So these six are consulted **only** if a response ever arrives carrying the code with no
   * message, and each still names the class of refusal and the way out.
   */
  /** 422 — starting or posting a run against a plan that is draft, cancelled or closed. */
  [ERROR_CODES.planNotConfirmed]:
    'This plan is not confirmed, so production cannot be recorded against it. Only a confirmed plan can take a run — a draft has to be confirmed first, and a cancelled or closed plan cannot be reopened.',
  /** 409 — the non-negative stock guard refused the posting (or the reversal). */
  [ERROR_CODES.insufficientStockForProduction]:
    'There is not enough material in stock to record this run, or reversing it would leave negative stock. Check the on-hand figures against the actual quantities entered.',
  /** 409 — expected exceeds on-hand and the plant has chosen to block rather than warn. */
  [ERROR_CODES.productionShortStockBlocked]:
    'This plant is configured to block a run when the mix needs more material than is in stock. Receive the short material first, or ask an administrator about the short-stock setting.',
  /*
   * `CONSUMPTION_VARIANCE_NOT_ACKNOWLEDGED` is **deliberately not in this map, and must never be
   * added to `MESSAGES`. Do not "fix" the omission.**
   *
   * One code carries **two different `details[]` shapes** (§5.2, corrected 2026-10-08). A
   * **variance breach** names the percentage the line is out by. An **off-recipe** line — a
   * material the mix called for none of — names **no percentage at all**, because `variancePct`
   * is `null` and there is none to name; it is a *separate gate*, and folding it into the
   * threshold test is a division by zero.
   *
   * `MESSAGES` is `Record<code, string>` — **one flat string per code** — and a flat string
   * cannot express both. An entry would render a percentage sentence over an off-recipe refusal
   * and reintroduce, in the UI, the exact defect the backend was corrected to avoid. The server
   * composes the right sentence for whichever gate fired, and `describeError`'s `error.message`
   * branch is how it reaches the user. Nothing is lost: only the two acknowledgement checkboxes
   * (F-3) clear this refusal, and the form binds to `details[]`, not to this file.
   */
  /** 422 — no finished-good item configured for the output, or it fails a property check. */
  [ERROR_CODES.finishedGoodNotConfigured]:
    'The block this plant produces has not been configured, so the output of a run cannot be added to stock. Set the finished block item in Settings → Mould setting.',
  /** 409 — reversing a run whose status is already `voided`. */
  [ERROR_CODES.productionRunAlreadyReversed]:
    'This run has already been reversed, so nothing further can be done with it. It keeps its number permanently; to correct the figures, record a new run.',
  /** 409 — cancelling or deleting a plan that has at least one posted run against it. */
  [ERROR_CODES.planHasProductionRuns]:
    'This plan has production recorded against it and cannot be cancelled or deleted. Reverse the runs first, or close the plan instead — closing keeps what was produced.',

  /*
   * --- feature 013's eight codes (013/design.md §2.9, task F1a).
   *
   * **Eight fallbacks here and no overrides in `MESSAGES`.** Every one of these refusals carries
   * its substance in a **server-composed** `message` that names a figure or a document:
   *
   *   413 ATTACHMENT_TOO_LARGE        "This upload is 10.5 MB; the limit is 10 MB per document."
   *   422 ATTACHMENT_LIMIT_REACHED    "GRN-2026-000015 already has 10 documents attached."
   *   409 ATTACHMENT_DUPLICATE        names the attachment already holding that content
   *   410 ATTACHMENT_REMOVED          names the file and the receipt it was withdrawn from
   *   410 ATTACHMENT_CONTENT_MISSING  names the file AND the remedy (restore from backup)
   *
   * `describeError` returns a `MESSAGES` hit **before** it looks at `error.message`, so an entry
   * there would replace the megabyte figure with a sentence naming nothing — which is exactly
   * what AC-J3 ("the message is actionable rather than a code") forbids. The 413's figure is the
   * whole **request's** size, which is why the server's wording says "upload" and not "file";
   * re-wording it here would also lose that distinction.
   *
   * So these eight are consulted **only** if a response ever arrives carrying the code with no
   * message, and each still names the class of refusal and the way out.
   */
  /** 422 — the content sniffer did not recognise the leading bytes as one of the six types. */
  [ERROR_CODES.attachmentTypeRejected]:
    'That file is not a PDF or an image. Attach a PDF, JPEG, PNG, WebP or HEIC — the kind is read from the file itself, so renaming it does not help.',
  /** 422 — an 11th live attachment on one receipt. */
  [ERROR_CODES.attachmentLimitReached]:
    'This receipt already holds the maximum number of documents. Remove one to free a slot, then attach this again.',
  /** 413 — the upload stream exceeded 10 MB and the server stopped reading it. */
  [ERROR_CODES.attachmentTooLarge]:
    'That upload is over the 10 MB limit for one document. If it is a photo, retake it at a lower resolution; if it is a scan, scan it at a lower quality.',
  /** 409 — the same content is already attached, live, to this receipt. */
  [ERROR_CODES.attachmentDuplicate]:
    'This exact document is already attached to this receipt. Nothing was added — the existing one is still there.',
  /** 410 — the attachment was deliberately removed, with an actor and a reason. */
  [ERROR_CODES.attachmentRemoved]:
    'This document was removed from the receipt, so it can no longer be opened. The removal was recorded with a reason; the list shows only current documents.',
  /** 410 — the record is intact and its file is not on the server. A restore, not a re-upload. */
  [ERROR_CODES.attachmentContentMissing]:
    'The stored file for this document is no longer on the server. The attachment record is intact; ask an administrator to restore the file from backup.',
  /** 500 — the disk write failed. Nothing was attached and the receipt is untouched. */
  [ERROR_CODES.attachmentStorageWriteFailed]:
    'The document could not be stored, so nothing was attached. Try again; if it keeps failing, tell an administrator — the server may be out of disk space.',
  /** 422 — a beyond-tolerance weight variance was posted without its confirmation. */
  [ERROR_CODES.weightVarianceNotAcknowledged]:
    'The weighed quantity is outside the allowed tolerance, so the variance has to be confirmed before this is saved. Confirm it to continue — a variance never blocks a posting.',
};

const FALLBACK = 'Something went wrong. Please try again.';

/** Turns anything a query or mutation threw into copy safe to show a user. */
export function describeError(error: unknown): string {
  if (isApiError(error)) {
    const override = MESSAGES[error.code];
    if (override !== undefined) return override;
    if (error.message) return error.message;
    return CODE_FALLBACK_MESSAGES[error.code] ?? FALLBACK;
  }
  if (error instanceof Error && error.message) return error.message;
  return FALLBACK;
}
